import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { LimitType, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const limitTypeCreateSchema = z.object({
  name: z.string().min(1, '締め日名は必須です'),
  priority: z.number().default(0),
  scopeSt: z.number().int().min(1).max(31),
  scopeEd: z.number().int().min(1).max(31),
});

export type LimitTypeCreateInput = z.infer<typeof limitTypeCreateSchema>;
export type LimitTypeUpdateInput = Partial<LimitTypeCreateInput>;

const COL = 'limitTypes';

// デフォルト固定締め日。Firestoreには初期保存せず、取得時にクライアント側で先頭に合成する。
// 編集・削除は不可（固定値）。表示順のみ並び替え・永続化できる。
const DEFAULT_LIMIT_TYPE_SEEDS = [
  { name: '月末締め', scopeSt: 1, scopeEd: 31 },
  { name: '20日締め', scopeSt: 21, scopeEd: 20 },
  { name: '15日締め', scopeSt: 16, scopeEd: 15 },
  { name: '10日締め', scopeSt: 11, scopeEd: 10 },
  { name: '5日締め', scopeSt: 6, scopeEd: 5 },
  { name: '随時', scopeSt: 1, scopeEd: 31 },
] as const;
const DEFAULT_LIMIT_TYPE_PREFIX = 'limittype-default-';

export const DEFAULT_LIMIT_TYPES: LimitType[] = DEFAULT_LIMIT_TYPE_SEEDS.map((s, i) => ({
  id: `${DEFAULT_LIMIT_TYPE_PREFIX}${i}`,
  name: s.name,
  scopeSt: s.scopeSt,
  scopeEd: s.scopeEd,
  priority: i,
  isDefault: true,
  isDeleted: false,
  createdBy: 'system',
  updatedBy: 'system',
})) as unknown as LimitType[];

export function isDefaultLimitType(id: ID): boolean {
  return id.startsWith(DEFAULT_LIMIT_TYPE_PREFIX);
}

export async function fetchLimitTypes(): Promise<LimitType[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as LimitType);
  // 永続化されたデフォルト締め日の並び順(priority)を反映する。
  // 未シードのデフォルトは正規の既定順にフォールバックする。
  const persisted = new Map(
    all.filter((x) => isDefaultLimitType(x.id)).map((x) => [x.id, x.priority])
  );
  const defaults = DEFAULT_LIMIT_TYPES.map((x) => ({
    ...x,
    priority: persisted.get(x.id) ?? x.priority,
  })).sort((a, b) => a.priority - b.priority);
  const custom = all.filter((x) => !isDefaultLimitType(x.id));
  // デフォルト固定締め日を先頭に、ユーザー登録締め日を後続に並べる
  return [...defaults, ...custom];
}

/**
 * デフォルト固定締め日の表示順を永続化する。
 * Firestore上に未シードの場合は setDoc(merge) で作成しつつ priority を更新する。
 * 種別名・締め範囲・isDefaultは固定値として常に書き込む。管理者のみ許可。
 */
export async function reorderDefaultLimitTypes(
  orderedIds: ID[],
  userId: ID
): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    const def = DEFAULT_LIMIT_TYPES.find((x) => x.id === id);
    if (!def) return;
    batch.set(
      doc(db, COL, id),
      {
        name: def.name,
        scopeSt: def.scopeSt,
        scopeEd: def.scopeEd,
        priority: index,
        isDefault: true,
        ...buildBaseFields(userId),
      },
      { merge: true }
    );
  });
  await batch.commit();
  await writeAuditLog({
    entityType: 'limitType',
    entityId: 'default-order',
    action: 'update',
    userId,
    after: { order: orderedIds },
  });
}

export async function fetchLimitType(id: ID): Promise<LimitType | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<LimitType>(snap);
}

export async function createLimitType(
  data: LimitTypeCreateInput,
  userId: ID
): Promise<LimitType> {
  const validated = parseOrThrow(limitTypeCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as LimitType;
  await writeAuditLog({
    entityType: 'limitType',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateLimitType(
  id: ID,
  data: LimitTypeUpdateInput,
  userId: ID
): Promise<LimitType> {
  if (isDefaultLimitType(id)) throw new Error('デフォルト締め日は編集できません。');
  const validated = limitTypeCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('締め日が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'limitType',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as LimitType;
}

export async function deleteLimitType(id: ID, userId: ID): Promise<void> {
  if (isDefaultLimitType(id)) throw new Error('デフォルト締め日は削除できません。');
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('締め日が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'limitType',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
